/**
 * Cross-tab session events (#2212, #2214, #2217).
 *
 * BroadcastChannel reaches every same-origin browsing context. A dedicated
 * localStorage key is the fallback when the channel is missing (older
 * browsers, some private modes): `storage` events fire in *other* tabs only.
 *
 * Lock and unlock are scoped to user + tenant + session family + lock
 * generation so a delayed message from a previous occupancy cannot open a
 * later occupant's overlay — including a later login of the same user.
 */

export const SESSION_LOGOUT_CHANNEL = 'staff-portal-session';
export const SESSION_LOGOUT_STORAGE_KEY = 'staff-portal-session-logout';

export const INACTIVITY_LOGOUT_REASON = 'inactivity';

export type SessionLogoutReason = typeof INACTIVITY_LOGOUT_REASON;

export type SessionLogoutMessage = {
  type: 'logout';
  reason?: SessionLogoutReason;
};

export type SessionLockScope = {
  userId: string;
  tenantId: string;
  sessionFamilyId: string;
  generation: number;
};

export type SessionLockMessage = SessionLockScope & { type: 'lock' };
export type SessionUnlockMessage = SessionLockScope & { type: 'unlock' };

export type SessionBroadcastMessage =
  | SessionLogoutMessage
  | SessionLockMessage
  | SessionUnlockMessage;

let broadcastSuppressed = false;

export function suppressSessionBroadcast(fn: () => void): void {
  broadcastSuppressed = true;
  try {
    fn();
  } finally {
    broadcastSuppressed = false;
  }
}

function isLogoutMessage(value: unknown): value is SessionLogoutMessage {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as { type?: unknown; reason?: unknown };
  if (record.type !== 'logout') return false;
  if (record.reason !== undefined && record.reason !== INACTIVITY_LOGOUT_REASON) {
    return false;
  }
  return true;
}

function isLockScope(record: {
  userId?: unknown;
  tenantId?: unknown;
  sessionFamilyId?: unknown;
  generation?: unknown;
}): boolean {
  return (
    typeof record.userId === 'string' &&
    record.userId !== '' &&
    typeof record.tenantId === 'string' &&
    record.tenantId !== '' &&
    typeof record.sessionFamilyId === 'string' &&
    record.sessionFamilyId !== '' &&
    typeof record.generation === 'number' &&
    Number.isInteger(record.generation) &&
    record.generation > 0
  );
}

function isLockMessage(value: unknown): value is SessionLockMessage {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as {
    type?: unknown;
    userId?: unknown;
    tenantId?: unknown;
    sessionFamilyId?: unknown;
    generation?: unknown;
  };
  return record.type === 'lock' && isLockScope(record);
}

function isUnlockMessage(value: unknown): value is SessionUnlockMessage {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as {
    type?: unknown;
    userId?: unknown;
    tenantId?: unknown;
    sessionFamilyId?: unknown;
    generation?: unknown;
  };
  return record.type === 'unlock' && isLockScope(record);
}

function parseBroadcastMessage(value: unknown): SessionBroadcastMessage | null {
  if (isLogoutMessage(value)) {
    return value.reason === undefined
      ? { type: 'logout' }
      : { type: 'logout', reason: value.reason };
  }
  if (isLockMessage(value)) {
    return {
      type: 'lock',
      userId: value.userId,
      tenantId: value.tenantId,
      sessionFamilyId: value.sessionFamilyId,
      generation: value.generation,
    };
  }
  if (isUnlockMessage(value)) {
    return {
      type: 'unlock',
      userId: value.userId,
      tenantId: value.tenantId,
      sessionFamilyId: value.sessionFamilyId,
      generation: value.generation,
    };
  }
  return null;
}

function buildLogoutMessage(reason?: SessionLogoutReason): SessionLogoutMessage {
  return reason === undefined ? { type: 'logout' } : { type: 'logout', reason };
}

function post(message: SessionBroadcastMessage): void {
  if (broadcastSuppressed) return;

  try {
    const channel = new BroadcastChannel(SESSION_LOGOUT_CHANNEL);
    channel.postMessage(message);
    channel.close();
  } catch {
    // Channel can be missing; storage fallback still runs.
  }

  try {
    localStorage.setItem(
      SESSION_LOGOUT_STORAGE_KEY,
      JSON.stringify({ ...message, at: Date.now() }),
    );
  } catch {
    // Private window / blocked site data: nothing else we can do.
  }
}

export function broadcastSessionLogout(reason?: SessionLogoutReason): void {
  post(buildLogoutMessage(reason));
}

export function broadcastSessionLock(scope: SessionLockScope): void {
  post({ type: 'lock', ...scope });
}

export function broadcastSessionUnlock(scope: SessionLockScope): void {
  post({ type: 'unlock', ...scope });
}

export function subscribeToSessionBroadcast(
  onMessage: (message: SessionBroadcastMessage) => void,
): () => void {
  let channel: BroadcastChannel | null = null;

  const handleMessage = (event: MessageEvent<unknown>) => {
    const parsed = parseBroadcastMessage(event.data);
    if (parsed !== null) onMessage(parsed);
  };

  try {
    channel = new BroadcastChannel(SESSION_LOGOUT_CHANNEL);
    channel.addEventListener('message', handleMessage);
  } catch {
    // Storage fallback only.
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== SESSION_LOGOUT_STORAGE_KEY || event.newValue === null) return;
    try {
      const parsed: unknown = JSON.parse(event.newValue);
      const message = parseBroadcastMessage(parsed);
      if (message !== null) {
        onMessage(message);
        return;
      }
    } catch {
      // Unreadable writes are ignored; they are not a logout signal.
    }
  };

  window.addEventListener('storage', handleStorage);

  return () => {
    if (channel !== null) {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    }
    window.removeEventListener('storage', handleStorage);
  };
}

export function subscribeToSessionLogout(
  onLogout: (message: SessionLogoutMessage) => void,
): () => void {
  return subscribeToSessionBroadcast((message) => {
    if (message.type === 'logout') onLogout(message);
  });
}
